import React, { useState, useEffect, useRef, useMemo } from "react";
import * as Chart from "chart.js";
import moment from "moment-hijri";
import HeadingComponent from "../shared/heading";

const { Chart: ChartJS, registerables } = Chart;
ChartJS.register(...registerables);

const ReusableKPIChart = ({
  chartData,
  title,
  subtitle,
  chartTitle,
  yAxisLabel = "",
  colors = {
    revenue: "5793C8",
    transaction: "#ff9933",
    atv: "#A5A5A5",
    als: "#ffd700",
  },
  labels = {
    revenue: "Monthly Revenue",
    transaction: "Monthly Transaction",
    atv: "ATV",
    als: "ALS",
  },
  showDataLabels = true,
  showRamadanRow = false,
  ramadanBorderColor = "#4C96D9",
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const containerRef = useRef(null);
  const tableRef = useRef(null);
  const labelColRef = useRef(null);


  const themeColors = {
    textColor: "#e5e7eb",
    gridColor: "rgba(255, 255, 255, 0.1)",
    borderColor: "#4b5563",
    backgroundColor: "#6B6A6C",
    labelBoxBg: "rgba(0, 0, 0, 0.4)",
    labelTextColor: "#ffffff",
    tooltipBg: "rgba(31, 41, 55, 0.95)",
    tooltipBorder: "#4b5563",
  };

  const yAxisFormatters = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return {
        left: (value) => value,
        right: (value) => value,
      };
    }
    return {
      left: (value) => value.toLocaleString(),
      right: (value) =>
        value % 1 === 0 ? value.toFixed(0) : value.toFixed(2),
    };
  }, [chartData]);

  const axisRanges = useMemo(() => {
    if (!chartData || chartData.length === 0)
      return {
        y: { min: undefined, max: undefined },
        y1: { min: undefined, max: undefined },
      };

    const pad = (vals, factor) => {
      const filtered = vals.filter((v) => v != null && !isNaN(v));
      if (!filtered.length) return { min: undefined, max: undefined };
      const mn = Math.min(...filtered);
      const mx = Math.max(...filtered);
      const range = mx - mn || Math.abs(mx) * 0.2 || 1;
      return {
        min: Math.max(0, Math.floor(mn - range * factor)),
        max: Math.ceil(mx + range * factor),
      };
    };

    const yRange = pad(
      [
        ...chartData.map((d) => d.revenue),
        ...chartData.map((d) => d.transaction),
      ],
      0.5
    );

    const atvVals = chartData
      .map((d) => d.atv)
      .filter((v) => v != null && !isNaN(v));
    const alsVals = chartData
      .map((d) => d.als)
      .filter((v) => v != null && !isNaN(v));

    const atvMax = atvVals.length ? Math.max(...atvVals) : 0;
    const alsMax = alsVals.length ? Math.max(...alsVals) : 0;
    const atvMin = atvVals.length ? Math.min(...atvVals) : 0;
    const alsMin = alsVals.length ? Math.min(...alsVals) : 0;

    const higherMax = Math.max(atvMax, alsMax);
    const y1Max = Math.ceil(higherMax / 0.3);
    const y1Range = y1Max - Math.min(atvMin, alsMin);
    const y1Min = -Math.ceil(y1Range * 0.1);

    return { y: yRange, y1: { min: y1Min, max: y1Max } };
  }, [chartData]);

  const groupDataByYear = (data) => {
    const grouped = {};
    data.forEach((item) => {
      if (!grouped[item.year]) grouped[item.year] = [];
      grouped[item.year].push(item);
    });
    return grouped;
  };

  const getRamadanMonths = useMemo(() => {
    if (!showRamadanRow || !chartData || chartData.length === 0) return [];
    const ramadanData = [];
    const monthMap = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
    };
    chartData.forEach((item) => {
      const monthName = item.month.split("-")[0];
      const year = parseInt(item.year);
      const monthNum =
        monthMap[monthName] !== undefined ? monthMap[monthName] : 0;
      const hijriYear = year - 579;
      const ramadanFirstDay = moment(
        `${hijriYear}-9-1`,
        "iYYYY-iM-iD"
      );
      const ramadanStartGregMonth = ramadanFirstDay.month();
      const ramadanStartGregYear = ramadanFirstDay.year();
      const isRamadan =
        ramadanStartGregYear === year &&
        ramadanStartGregMonth === monthNum;
      ramadanData.push({ isRamadan, monthName, year });
    });
    return ramadanData;
  }, [chartData, showRamadanRow]);

  useEffect(() => {
    if (chartData.length === 0 || !chartRef.current) return;
    if (chartInstance.current) chartInstance.current.destroy();

    const ctx = chartRef.current.getContext("2d");

    // ─── Shared state between plugins ────────────────────────────────────────
    const finalLabelRects = []; // populated by revenue + line label plugins, read by ramadanPlugin

    const ramadanMonthIndices = new Set();
    if (showRamadanRow && getRamadanMonths.length > 0) {
      getRamadanMonths.forEach((info, idx) => {
        if (info.isRamadan) ramadanMonthIndices.add(idx);
      });
    }

    const groupedByYear = groupDataByYear(chartData);
    const yearRanges = Object.keys(groupedByYear).map((year) => ({
      year,
      span: groupedByYear[year].length,
    }));
    const monthNames = chartData.map((d) => d.month.split("-")[0]);
    const ramadanFlags = getRamadanMonths.map((r) => r.isRamadan);

    // ─── customLegendPlugin ──────────────────────────────────────────────────
    const customLegendPlugin = {
      id: "customLegend",
      afterDraw(chart) {
        const legend = chart.legend;
        if (!legend || !legend.legendItems) return;
        const ctx = chart.ctx;
        ctx.save();
        legend.legendItems.forEach((item, i) => {
          const hitBox = legend.legendHitBoxes && legend.legendHitBoxes[i];
          if (!hitBox) return;
          const centerY = hitBox.top + hitBox.height / 2;
          const iconW = 16;
          const iconX = hitBox.left;
          ctx.clearRect(iconX, hitBox.top, iconW + 2, hitBox.height);
          if (i === 0) {
            ctx.fillStyle = colors.revenue;
            ctx.fillRect(iconX, centerY - 4, iconW, 8);
          } else if (i === 1) {
            ctx.strokeStyle = colors.transaction;
            ctx.lineWidth = 2;
            ctx.setLineDash([]);
            ctx.beginPath();
            ctx.moveTo(iconX, centerY);
            ctx.lineTo(iconX + iconW, centerY);
            ctx.stroke();
            ctx.fillStyle = colors.transaction;
            ctx.beginPath();
            ctx.arc(iconX + iconW / 2, centerY, 3, 0, Math.PI * 2);
            ctx.fill();
          } else if (i === 2) {
            ctx.strokeStyle = colors.atv;
            ctx.lineWidth = 2;
            ctx.setLineDash([]);
            ctx.beginPath();
            ctx.moveTo(iconX, centerY);
            ctx.lineTo(iconX + iconW, centerY);
            ctx.stroke();
          } else if (i === 3) {
            ctx.strokeStyle = colors.als;
            ctx.lineWidth = 2;
            ctx.setLineDash([3, 4]);
            ctx.beginPath();
            ctx.moveTo(iconX, centerY);
            ctx.lineTo(iconX + iconW, centerY);
            ctx.stroke();
            ctx.setLineDash([]);
          }
        });
        ctx.restore();
      },
    };

    // ─── shadowPlugin ────────────────────────────────────────────────────────
    const shadowPlugin = {
      id: "shadowPlugin",
      afterDatasetDraw(chart, args) {
        const { ctx } = chart;
        const dsIndex = args.index;
        ctx.save();
        if (dsIndex === 0) {
          ctx.shadowColor = colors.revenue + "99";
          ctx.shadowBlur = 25;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
        } else if (dsIndex === 1) {
          ctx.shadowColor = colors.transaction + "BB";
          ctx.shadowBlur = 10;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 4;
        } else if (dsIndex === 2) {
          ctx.shadowColor = "#ffffff";
          ctx.shadowBlur = 14;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
        } else if (dsIndex === 3) {
          ctx.shadowColor = colors.als + "CC";
          ctx.shadowBlur = 12;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 4;
        }
      },
      afterDatasetDraw(chart) {
        const { ctx } = chart;
        ctx.shadowColor = "transparent";
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        ctx.restore();
      },
    };

    // ─── FIXED: revenueLabelPlugin ── multi-pass collision like ChartTableComponent
    const revenueLabelPlugin = {
      id: "revenueLabels",
      afterDatasetsDraw(chart) {
        if (!showDataLabels) return;

        // Clear shared rect state
        finalLabelRects.length = 0;

        const revenueDataset = chart.data.datasets[0];
        const meta = chart.getDatasetMeta(0);
        if (!meta || meta.hidden) return;

        const ctx = chart.ctx;
        const chartTop = chart.scales.y.top + 4;
        const chartBottom = chart.scales.y.bottom - 4;
        const fontSize = 10;
        const PAD = 3;
        const BOX_H = 16;
        const BOX_BG = "#333333";

        ctx.font = `bold ${fontSize}px Arial`;

        // ── Step 1: Collect all label entries ─────────────────────────────
        const labels = [];
        meta.data.forEach((bar, index) => {
          const value = revenueDataset.data[index];
          if (value == null || value === 0) return;
          if (ramadanMonthIndices.has(index)) return;

          const text = value.toLocaleString();
          const textWidth = ctx.measureText(text).width;
          const boxW = textWidth + PAD * 2 + 8;

          // Default: above bar
          const baseYOffset = -BOX_H - 8;

          labels.push({
            x: bar.x,
            y: bar.y, // anchor = top of bar pixel
            text,
            color: colors.revenue,
            width: boxW,
            height: BOX_H,
            baseY: bar.y + baseYOffset,
            index,
          });
        });

        // ── Step 2: Initialise finalY to baseY ────────────────────────────
        labels.forEach((l) => { l.finalY = l.baseY; });

        // ── Step 3: Multi-pass iterative push (same as ChartTableComponent) ─
        const V_GAP = 4;
        const H_GAP = 3;

        const rectsOverlap = (a, b) => {
          const aL = a.x - a.width / 2 - H_GAP;
          const aR = a.x + a.width / 2 + H_GAP;
          const aT = a.finalY - a.height / 2 - V_GAP;
          const aB = a.finalY + a.height / 2 + V_GAP;
          const bL = b.x - b.width / 2;
          const bR = b.x + b.width / 2;
          const bT = b.finalY - b.height / 2;
          const bB = b.finalY + b.height / 2;
          return !(aR < bL || aL > bR || aB < bT || aT > bB);
        };

        const MAX_PASSES = 50;
        for (let pass = 0; pass < MAX_PASSES; pass++) {
          let anyMoved = false;
          for (let i = 0; i < labels.length; i++) {
            for (let j = i + 1; j < labels.length; j++) {
              const a = labels[i];
              const b = labels[j];
              if (!rectsOverlap(a, b)) continue;

              anyMoved = true;
              const aAbove = a.finalY < a.y;
              const bAbove = b.finalY < b.y;

              const overlapAmt =
                a.finalY + a.height / 2 + V_GAP -
                (b.finalY - b.height / 2);

              const clampT = chartTop + 4;
              const clampB = chartBottom - 4;

              if (
                Math.abs(a.x - b.x) <
                (a.width + b.width) / 2
              ) {
                if (aAbove && bAbove) {
                  if (a.finalY <= b.finalY) {
                    a.finalY = Math.max(clampT, a.finalY - overlapAmt / 2);
                    b.finalY = Math.min(clampB, b.finalY + overlapAmt / 2);
                  } else {
                    b.finalY -= overlapAmt / 2;
                    a.finalY += overlapAmt / 2;
                  }
                } else if (!aAbove && !bAbove) {
                  if (a.finalY >= b.finalY) {
                    b.finalY -= overlapAmt / 2;
                    a.finalY += overlapAmt / 2;
                  } else {
                    a.finalY = Math.max(clampT, a.finalY - overlapAmt / 2);
                    b.finalY = Math.min(clampB, b.finalY + overlapAmt / 2);
                  }
                } else {
                  if (aAbove) {
                    a.finalY = Math.max(clampT, a.finalY - overlapAmt / 2);
                    b.finalY = Math.min(clampB, b.finalY + overlapAmt / 2);
                  } else {
                    a.finalY = Math.max(clampT, a.finalY - overlapAmt / 2);
                    b.finalY = Math.min(clampB, b.finalY + overlapAmt / 2);
                  }
                }
              }
            }
          }
          if (!anyMoved) break;
        }

        // ── Step 4: Draw + register rects ─────────────────────────────────
        ctx.save();
        labels.forEach((lbl) => {
          if (lbl.finalY == null || isNaN(lbl.finalY)) return;

          const boxX = lbl.x - lbl.width / 2;
          const boxY = lbl.finalY - lbl.height / 2;

          // Register for ramadanPlugin
          finalLabelRects.push({
            left: boxX - 8,
            right: boxX + lbl.width + 8,
            top: boxY - 8,
            bottom: boxY + lbl.height + 8,
          });

          // ctx.fillStyle = BOX_BG;
          // ctx.fillRect(boxX - 1, boxY - 1, lbl.width + 2, lbl.height + 2);
          ctx.strokeStyle = lbl.color;
          ctx.lineWidth = 1.2;
          ctx.strokeRect(boxX, boxY, lbl.width, lbl.height);
          ctx.fillStyle = "#ffffff";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.font = `bold ${fontSize}px Arial`;
          ctx.fillText(lbl.text, lbl.x, lbl.finalY);
        });
        ctx.restore();
      },
    };

    // ─── FIXED: lineLabelPlugin ── multi-pass collision like ChartTableComponent
    const lineLabelPlugin = {
      id: "lineLabels",
      afterDatasetsDraw(chart) {
        if (!showDataLabels) return;

        const ctx = chart.ctx;
        const chartTop = chart.scales.y.top + 4;
        const chartBottom = chart.scales.y.bottom - 4;
        const fontSize = 9;
        const PAD = 2;
        const BOX_BG = "#333333";

        // ── Step 1: Collect all line label entries ─────────────────────────
        const labelsList = [];

        chart.data.datasets.forEach((dataset, dsIndex) => {
          if (dsIndex === 0) return; // skip bar (revenue handled by revenueLabelPlugin)
          const meta = chart.getDatasetMeta(dsIndex);
          if (!meta || !meta.data || meta.hidden) return;

          meta.data.forEach((point, pointIndex) => {
            const value = dataset.data[pointIndex];
            if (value == null || value === 0) return;

            let text = "";
            let dsType = "";
            if (dsIndex === 1) {
              text = Math.round(value).toLocaleString();
              dsType = "transaction";
            } else if (dsIndex === 2) {
  text = value.toFixed(2);
  dsType = "atv";
} else if (dsIndex === 3) {
              text = value.toFixed(2);
              dsType = "als";
            }
            if (!text) return;

            ctx.font = `bold ${fontSize}px Arial`;
            const textWidth = ctx.measureText(text).width;
            const boxW = textWidth + 8;
            const boxH = 13;

            // Default position: above the point
            const baseYOffset = -boxH - 6;

            labelsList.push({
              x: point.x,
              y: point.y, // anchor
              text,
              color: dataset.borderColor || "#888",
              width: boxW,
              height: boxH,
              baseY: point.y + baseYOffset,
              dsType,
              dsIndex,
              pointIndex,
            });
          });
        });

        // ── Step 2: Initialise finalY ──────────────────────────────────────
        labelsList.forEach((l) => { l.finalY = l.baseY; });

        // ── Step 3: Build obstacle list from already-placed revenue rects ──
        const revenueObstacles = finalLabelRects.map((r) => ({
          x: (r.left + r.right) / 2,
          y: (r.top + r.bottom) / 2,
          width: r.right - r.left,
          height: r.bottom - r.top,
          finalY: (r.top + r.bottom) / 2,
        }));

        // Combined list for collision checks
        const allLabels = [...revenueObstacles, ...labelsList];

        // ── Step 4: Multi-pass iterative push ─────────────────────────────
        const V_GAP = 3;
        const H_GAP = 2;

        const rectsOverlap = (a, b) => {
          const aL = a.x - a.width / 2 - H_GAP;
          const aR = a.x + a.width / 2 + H_GAP;
          const aT = a.finalY - a.height / 2 - V_GAP;
          const aB = a.finalY + a.height / 2 + V_GAP;
          const bL = b.x - b.width / 2;
          const bR = b.x + b.width / 2;
          const bT = b.finalY - b.height / 2;
          const bB = b.finalY + b.height / 2;
          return !(aR < bL || aL > bR || aB < bT || aT > bB);
        };

        const MAX_PASSES = 50;
        // Only move labelsList entries (revenue obstacles are fixed)
        for (let pass = 0; pass < MAX_PASSES; pass++) {
          let anyMoved = false;
          for (let i = 0; i < allLabels.length; i++) {
            for (let j = i + 1; j < allLabels.length; j++) {
              const a = allLabels[i];
              const b = allLabels[j];
              if (!rectsOverlap(a, b)) continue;

              // At least one must be a movable line label
              const aMovable = i >= revenueObstacles.length;
              const bMovable = j >= revenueObstacles.length;
              if (!aMovable && !bMovable) continue;

              anyMoved = true;

              const overlapAmt =
                a.finalY + a.height / 2 + V_GAP -
                (b.finalY - b.height / 2);

              const clampT = chartTop + 2;
              const clampB = chartBottom - 2;

              if (
                Math.abs(a.x - b.x) <
                (a.width + b.width) / 2
              ) {
                const aAbove = a.finalY < (a.y ?? a.finalY);
                const bAbove = b.finalY < (b.y ?? b.finalY);

                if (!aMovable) {
                  // a is fixed revenue label — push b only
                  b.finalY = Math.min(clampB, b.finalY + overlapAmt);
                } else if (!bMovable) {
                  // b is fixed revenue label — push a only
                  a.finalY = Math.max(clampT, a.finalY - overlapAmt);
                } else {
                  // Both movable: split the push
                  if (aAbove || !bAbove) {
                    a.finalY = Math.max(clampT, a.finalY - overlapAmt / 2);
                    b.finalY = Math.min(clampB, b.finalY + overlapAmt / 2);
                  } else {
                    b.finalY = Math.max(clampT, b.finalY - overlapAmt / 2);
                    a.finalY = Math.min(clampB, a.finalY + overlapAmt / 2);
                  }
                }
              }
            }
          }
          if (!anyMoved) break;
        }

        // ── Step 5: Draw labels ────────────────────────────────────────────
        ctx.save();
        // Only draw the labelsList portion (not revenue obstacles)
        labelsList.forEach((lbl) => {
          if (lbl.finalY == null || isNaN(lbl.finalY)) return;

          const boxX = lbl.x - lbl.width / 2;
          const boxY = lbl.finalY - lbl.height / 2;

          // Register in finalLabelRects so ramadanPlugin avoids these too
          finalLabelRects.push({
            left: boxX - 4,
            right: boxX + lbl.width + 4,
            top: boxY - 4,
            bottom: boxY + lbl.height + 4,
          });

          // Box only for transaction (same as original)
          if (lbl.dsType === "transaction") {
            // ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
            // ctx.fillRect(boxX, boxY, lbl.width, lbl.height);
            ctx.strokeStyle = lbl.color;
            ctx.lineWidth = 1;
            ctx.strokeRect(boxX, boxY, lbl.width, lbl.height);
          }

          ctx.fillStyle = "#ffffff";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.font = `bold ${fontSize}px Arial`;
          ctx.fillText(lbl.text, lbl.x, lbl.finalY);
        });
        ctx.restore();
      },
    };

    // ─── ramadanPlugin ────────────────────────────────────────────────────────
    const ramadanPlugin = (() => {
      const hasRamadan = showRamadanRow && ramadanFlags.some(Boolean);
      if (!hasRamadan)
        return { id: "ramadanPlugin_disabled", afterDraw() {} };

      return {
        id: "ramadanPlugin",
        beforeDraw (chart) {
          const ctx = chart.ctx;
          const xAxis = chart.scales.x;
          const yAxis = chart.scales.y;

          const BOX_H = 24;
          const ARROW_W = 14;
          const H_PAD = 6;
          const V_PAD = 6;
          const BR = 4;
          const LABEL_FONT = "bold 9px Arial";
          const LABEL_H_PAD = 11; // horizontal padding inside box

          const chartTop = yAxis.top;
          const chartBottom = yAxis.bottom;
          const chartLeft = xAxis.left;
          const chartRight = xAxis.right;

          // clampX takes boxW per-call so each label uses its own width
          const clampX = (x, boxW) =>
            Math.max(chartLeft + 4, Math.min(chartRight - boxW - 4, x));
          const clampY = (y) =>
            Math.max(chartTop + 4, Math.min(chartBottom - BOX_H - 4, y));

          const mkRect = (l, t, w, h) => ({
            left: l,
            right: l + w,
            top: t,
            bottom: t + h,
          });

          const overlaps = (a, b, hPad = H_PAD, vPad = V_PAD) =>
            a.left - hPad < b.right + hPad &&
            a.right + hPad > b.left - hPad &&
            a.top - vPad < b.bottom + vPad &&
            a.bottom + vPad > b.top - vPad;

          // Separate obstacle types — different padding per type for accurate placement
          const valueObs = [];   // value labels (tight — avoid visual overlap only)
          const barObs = [];     // bar tops (medium — don't sit on top of bar)

          finalLabelRects.forEach((r) =>
            valueObs.push({ left: r.left, right: r.right, top: r.top, bottom: r.bottom })
          );

          chart.data.datasets.forEach((dataset, dsIdx) => {
            if (dataset.type !== "bar") return;
            const meta = chart.getDatasetMeta(dsIdx);
            if (meta.hidden) return;
            meta.data.forEach((bar) => {
              if (!bar) return;
              try {
                const props = bar.getProps(["x", "y", "base", "width"], true);
                const bL = props.x - props.width / 2;
                const bR = props.x + props.width / 2;
                const bTop = Math.min(props.y, props.base);
                if (!isNaN(bL) && !isNaN(bTop)) {
                  barObs.push({ left: bL - 2, right: bR + 2, top: bTop - 2, bottom: bTop + 30 });
                }
              } catch (_) {}
            });
          });

          const isBlocked = (testRect) =>
            placedBoxes.some((obs) => overlaps(testRect, obs, 6, 6)) ||
            barObs.some((obs) => overlaps(testRect, obs, 2, 2)) ||
            valueObs.some((obs) => overlaps(testRect, obs, 2, 2));

          const getBarTopY = (idx) => {
            const meta = chart.getDatasetMeta(0);
            if (meta?.data?.[idx]) {
              const bar = meta.data[idx];
              try {
                const props = bar.getProps(["y", "base"], true);
                return Math.min(props.y, props.base ?? props.y);
              } catch (_) {
                return Math.min(bar.y, bar.base ?? bar.y);
              }
            }
            for (let i = 1; i < chart.data.datasets.length; i++) {
              const m = chart.getDatasetMeta(i);
              if (m.hidden) continue;
              if (m?.data?.[idx] && !isNaN(m.data[idx].y))
                return m.data[idx].y;
            }
            return yAxis.getPixelForValue(0);
          };

          const items = [];
          let mi = 0;
          yearRanges.forEach((yr) => {
            for (let i = 0; i < yr.span; i++) {
              if (ramadanFlags[mi]) {
                const val = chartData[mi]?.revenue ?? 0;
                const dataX = xAxis.getPixelForValue(mi);
                const dataY = getBarTopY(mi);
                items.push({
                  label: `RAMADHAN ${yr.year} ${monthNames[mi]} : ${val.toLocaleString()}`,
                  dataX,
                  dataY,
                  revenue: val,
                });
              }
              mi++;
            }
          });

          if (!items.length) return;

          const chartMidX = (chartLeft + chartRight) / 2;
          const chartH = chartBottom - chartTop;

          const placedBoxes = [];

          items.forEach(({ label, dataX, dataY }) => {
            const arrowTipY = dataY;
            let chosenX = null;
            let chosenY = null;
            let placed = false;

            // Per-label width based on actual text measurement
            ctx.font = LABEL_FONT;
            const BOX_W = ctx.measureText(label).width + LABEL_H_PAD * 2;

            // Start from top of chart and work downward — labels go as high as possible
            const vertGaps = [];
const step = 8;
const MIN_GAP = 55; // <-- ye value badha/ghata ke height adjust kar sakte ho
for (let d = MIN_GAP; d <= chartH - 6; d += step) vertGaps.push(Math.round(d));

            // Direction-aware: right-half bars try left first
            // ✅ Pure vertical (dx=0) priority — box seedha upar jaaye, side shift sirf last resort
           // ✅ Pure vertical (dx=0) priority — box seedha upar jaaye, side shift sirf last resort
            const s = BOX_W * 0.3;
            const horizShifts = dataX > chartMidX
              ? [0, -s, s, -2*s, 2*s, -3*s, 3*s, -4*s, 4*s, -5*s, 5*s, -6*s, 6*s]
              : [0, s, -s, 2*s, -2*s, 3*s, -3*s, 4*s, -4*s, 5*s, -5*s, 6*s, -6*s];
            const candidateOffsets = [];
            // Pass 1: sabhi heights pe pehle sirf seedha upar (dx = 0) try karo
            vertGaps.forEach((dy) => candidateOffsets.push([0, dy]));
            // Pass 2: sirf tab horizontal shift try ho jab koi bhi vertical-only spot na mila ho
            vertGaps.forEach((dy) =>
              horizShifts.forEach((dx) => {
                if (dx !== 0) candidateOffsets.push([dx, dy]);
              })
            );

            for (const [dx, dy] of candidateOffsets) {
              const candidateBoxX = clampX(dataX + dx - BOX_W / 2, BOX_W);
              const candidateBoxY = clampY(arrowTipY - dy - BOX_H);
              const testRect = mkRect(candidateBoxX, candidateBoxY, BOX_W, BOX_H);
              if (!isBlocked(testRect)) {
                chosenX = candidateBoxX;
                chosenY = candidateBoxY;
                placed = true;
                break;
              }
            }

            if (!placed) {
              // Fallback: least-overlap position
              let bestScore = Infinity;
              let bestX = clampX(dataX - BOX_W / 2, BOX_W);
              let bestY = chartTop + 6;
              candidateOffsets.slice(0, 80).forEach(([dx, dy]) => {
                const bx = clampX(dataX + dx - BOX_W / 2, BOX_W);
                const by = clampY(arrowTipY - dy - BOX_H);
                const r = mkRect(bx, by, BOX_W, BOX_H);
                const score =
                  placedBoxes.filter((o) => overlaps(r, o, 0, 0)).length * 10 +
                  barObs.filter((o) => overlaps(r, o, 0, 0)).length * 5 +
                  valueObs.filter((o) => overlaps(r, o, 0, 0)).length;
                if (score < bestScore) { bestScore = score; bestX = bx; bestY = by; }
              });
              chosenX = bestX;
              chosenY = bestY;
            }

            const chosenRect = mkRect(chosenX, chosenY, BOX_W, BOX_H);
            placedBoxes.push(chosenRect);
            // Register placed Ramadan box so next labels avoid it
            valueObs.push({
              left: chosenRect.left - H_PAD,
              right: chosenRect.right + H_PAD,
              top: chosenRect.top - V_PAD,
              bottom: chosenRect.bottom + V_PAD,
            });

            const bTop = chosenY;
            const bBot = chosenY + BOX_H;
            const baseCX = Math.min(
              Math.max(dataX, chosenX + ARROW_W / 2 + BR),
              chosenX + BOX_W - ARROW_W / 2 - BR
            );
            const baseBL = baseCX - ARROW_W / 2;
            const baseBR = baseCX + ARROW_W / 2;

            const drawShape = () => {
              ctx.beginPath();
              ctx.moveTo(chosenX + BR, bTop);
              ctx.lineTo(chosenX + BOX_W - BR, bTop);
              ctx.arcTo(
                chosenX + BOX_W,
                bTop,
                chosenX + BOX_W,
                bTop + BR,
                BR
              );
              ctx.lineTo(chosenX + BOX_W, bBot);
              ctx.lineTo(baseBR, bBot);
              ctx.lineTo(dataX, arrowTipY);
              ctx.lineTo(baseBL, bBot);
              ctx.lineTo(chosenX, bBot);
              ctx.lineTo(chosenX, bTop + BR);
              ctx.arcTo(chosenX, bTop, chosenX + BR, bTop, BR);
              ctx.closePath();
            };

            ctx.save();

// 🔥 Shadow ONLY for border + arrow
ctx.shadowColor = "rgba(0,0,0,0.6)";
ctx.shadowBlur = 8;
ctx.shadowOffsetX = 0;
ctx.shadowOffsetY = 3;

// Draw border + arrow with shadow
ctx.strokeStyle = colors.transaction;
ctx.lineWidth = 1.5;
drawShape();
ctx.stroke();

// ❌ NO fill (transparent box)
ctx.shadowColor = "transparent"; // reset shadow for text

// 🔥 Text shadow (light for readability)
ctx.shadowColor = "rgba(0,0,0,0.7)";
ctx.shadowBlur = 3;
ctx.shadowOffsetX = 0;
ctx.shadowOffsetY = 1;

ctx.fillStyle = "#ffffff";
ctx.textAlign = "center";
ctx.textBaseline = "middle";
ctx.font = LABEL_FONT;
ctx.fillText(label, chosenX + BOX_W / 2, chosenY + BOX_H / 2);

ctx.restore();
          });
        },
      };
    })();

    // ─── alignTablePlugin ── keeps the table's month columns lined up under the
    // matching bar by measuring the chart's real plot-area pixels and sizing the
    // table's label column + overall width to match it exactly.
    const alignTablePlugin = {
      id: "alignTable",
      afterLayout(chart) {
        const area = chart.chartArea;
        if (!area || !containerRef.current || !tableRef.current) return;

        const canvasRect = containerRef.current.getBoundingClientRect();
        const tableRect = tableRef.current.getBoundingClientRect();

        const targetLeft = canvasRect.left + area.left;
        const targetRight = canvasRect.left + area.right;

        const labelWidth = Math.max(110, targetLeft - tableRect.left);
        const totalWidth = labelWidth + (targetRight - targetLeft);

        tableRef.current.style.width = `${totalWidth}px`;
        if (labelColRef.current) {
          labelColRef.current.style.width = `${labelWidth}px`;
        }
      },
    };

    // ─── Chart instance ───────────────────────────────────────────────────────
    chartInstance.current = new ChartJS(ctx, {
      type: "bar",
      data: {
        labels: chartData.map((d) => d.month),
        datasets: [
          {
            type: "bar",
            label: labels.revenue,
            data: chartData.map((d) => d.revenue),
            borderColor: colors.revenue,
            borderWidth: 1.5,
            maxBarThickness: 16,
            categoryPercentage: 1.0,
            barPercentage: 0.9,
            yAxisID: "y",
          },
          {
            type: "line",
            label: labels.transaction,
            data: chartData.map((d) => d.transaction),
            borderColor: colors.transaction,
            backgroundColor: colors.transaction,
            borderWidth: 1.5,
            pointRadius: 2,
            pointBackgroundColor: colors.transaction,
            yAxisID: "y",
          },
          {
            type: "line",
            label: labels.atv,
            data: chartData.map((d) => d.atv),
            borderColor: colors.atv,
            backgroundColor: colors.atv,
            borderWidth: 2,
            pointRadius: 0,
            pointBackgroundColor: colors.atv,
            yAxisID: "y1",
          },
          {
            type: "line",
            label: labels.als,
            data: chartData.map((d) => d.als),
            borderColor: colors.als,
            backgroundColor: colors.als,
            borderWidth: 2,
            pointRadius: 0,
            pointBackgroundColor: colors.als,
            yAxisID: "y1",
            borderDash: [3, 4],
          },
        ],
      },
      options: {
        responsive: false,
        maintainAspectRatio: false,
        devicePixelRatio: Math.max(window.devicePixelRatio || 1, 2),
        interaction: { mode: null },
        hover: { mode: null },
        events: [],
        plugins: {
          legend: {
            display: true,
            position: "top",
            labels: {
              color: "#ffffff",
              usePointStyle: false,
              boxWidth: 16,
              boxHeight: 8,
              padding: 8,
              font: { size: 10 },
              generateLabels: (chart) =>
                chart.data.datasets.map((ds, i) => ({
                  text: ds.label,
                  datasetIndex: i,
                  hidden: false,
                  fillStyle: "transparent",
                  strokeStyle: "transparent",
                  lineWidth: 0,
                  fontColor: "#ffffff",
                })),
            },
          },
          tooltip: { enabled: false },
        },
        scales: {
          x: {
            offset: true,
            grid: { display: false },
            ticks: {
              display: false,
              color: themeColors.textColor,
              font: { size: 10 },
            },
          },
          y: {
            type: "linear",
            position: "left",
            min: axisRanges.y.min,
            max: axisRanges.y.max,
            ticks: {
              color: themeColors.textColor,
              font: { size: 10 },
              callback: yAxisFormatters.left,
            },
            grid: { display: false },
          },
          y1: {
            type: "linear",
            position: "right",
            min: axisRanges.y1.min,
            max: axisRanges.y1.max,
            ticks: {
              color: themeColors.textColor,
              font: { size: 10 },
              callback: yAxisFormatters.right,
            },
            grid: { display: false },
          },
        },
        layout: { padding: { left: 24, right: 24, top: 1, bottom: 4 } },
      },
      plugins: [
        customLegendPlugin,
        shadowPlugin,
        revenueLabelPlugin,
        lineLabelPlugin,
        ramadanPlugin,
        alignTablePlugin,
      ],
    });

   const resizeObserver = new ResizeObserver(() => {
  if (!containerRef.current || !chartInstance.current) return;
  const w = containerRef.current.offsetWidth;
  const h = containerRef.current.offsetHeight;
  chartInstance.current.resize(w, h);
});
if (containerRef.current) resizeObserver.observe(containerRef.current);

return () => {
  resizeObserver.disconnect();
  if (chartInstance.current) chartInstance.current.destroy();
};
  }, [
    chartData,
    colors,
    labels,
    showDataLabels,
    yAxisFormatters,
    showRamadanRow,
    ramadanBorderColor,
    getRamadanMonths,
    axisRanges,
  ]);

  if (chartData.length === 0) return null;

  const groupedChartData = groupDataByYear(chartData);
  const months = chartData.map((d) => d.month.split("-")[0]);
  const yearRanges = Object.keys(groupedChartData).map((year) => ({
    year,
    span: groupedChartData[year].length,
  }));

  const RevenueIndicator = () => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        marginRight: 4,
        verticalAlign: "middle",
      }}
    >
      <span
        style={{
          display: "inline-block",
          width: 14,
          height: 8,
          backgroundColor: colors.revenue,
          borderRadius: 1,
        }}
      />
    </span>
  );

  const TransactionIndicator = () => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        marginRight: 4,
        verticalAlign: "middle",
        position: "relative",
        width: 20,
        height: 10,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: "50%",
          left: 0,
          right: 0,
          height: 2,
          backgroundColor: colors.transaction,
          transform: "translateY(-50%)",
        }}
      />
      <span
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: colors.transaction,
        }}
      />
    </span>
  );

  const ATVIndicator = () => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        marginRight: 4,
        verticalAlign: "middle",
      }}
    >
      <span
        style={{
          display: "inline-block",
          width: 18,
          height: 2,
          backgroundColor: colors.atv,
        }}
      />
    </span>
  );

  const ALSIndicator = () => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        marginRight: 4,
        verticalAlign: "middle",
      }}
    >
      <svg width="20" height="10" viewBox="0 0 20 10">
        <line
          x1="0"
          y1="5"
          x2="20"
          y2="5"
          stroke={colors.als}
          strokeWidth="2"
          strokeDasharray="3,4"
        />
      </svg>
    </span>
  );

  return (
    <div className="w-full h-full flex items-center justify-center px-4">
      <div
        className="w-full mx-auto px-8 py-8 rounded-lg shadow-lg bg-card"
        style={{ backgroundColor: "#ffffff" }}
      >
        <div
          className="text-center"
          style={{
            backgroundColor: "#ffffff",
            padding: "0.1rem 2rem 0.25rem 2rem",
          }}
        >
          {title && (
            <div style={{ margin: 0, lineHeight: 1.1 }}>
              <HeadingComponent variant={"main"}>{title}</HeadingComponent>
            </div>
          )}
          {subtitle && (
            <div style={{ margin: 0, lineHeight: 1.1 }}>
              <HeadingComponent variant={"sub"}>{subtitle}</HeadingComponent>
            </div>
          )}
        </div>

        <div
          className="w-full p-3"
          style={{ backgroundColor: "#464647", borderRadius: "8px" }}
        >
          <div
            className="rounded-t-lg pb-0"
            style={{
              paddingLeft: "50px",
              paddingRight: "2px",
              paddingTop: "16px",
            }}
          >
            <h3
              className="text-center text-base font-semibold mb-0"
              style={{ color: "#ffffff" }}
            >
              {chartTitle}
            </h3>
            <div className="ml-3 h-[350px] relative" ref={containerRef}>

            {yAxisLabel && (
              <div
                style={{
                  position: "absolute",
                  left: -30,
                  top: "50%",
                  transform:
                    "translateY(-50%) rotate(-90deg)",
                  color: "#ffffff",
                  fontSize: "10px",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  transformOrigin: "center center",
                }}
              >
                {yAxisLabel}
              </div>
            )}
            <canvas ref={chartRef}></canvas>
          </div>
        </div>

        <div className="w-full p-3" style={{ marginLeft: "-20px" }}>
          <div className="overflow-x-auto">
            <table
              ref={tableRef}
              className="!border-collapse text-xs table-fixed"
            >
              <colgroup>
                <col ref={labelColRef} style={{ width: "180px" }} />
                {months.map((_, idx) => (
                  <col key={idx} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th className="py-0.5 px-1 text-center font-normal"></th>
                  {months.map((month, idx) => (
                    <th
                      key={idx}
                      className="!border !border-border px-0.5 py-0.5 text-center font-normal text-[9px]"
                      style={{ color: "#ffffff", borderColor: "#4b5563" }}
                    >
                      {month}
                    </th>
                  ))}
                </tr>
                <tr>
                  <td className="py-0.5 px-1 text-center text-[9px]"></td>
                  {yearRanges.map((range, idx) => (
                    <td
                      key={idx}
                      colSpan={range.span}
                      className="!border !border-border px-0.5 py-0.5 text-center text-[9px] font-bold"
                      style={{ color: "#ffffff", borderColor: "#4b5563" }}
                    >
                      {range.year}
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-muted/20 transition-colors">
                  <td
                    className="!border !border-border px-1 py-1 font-normal text-[9px] text-left whitespace-nowrap"
                    style={{
                      color: "#ffffff",
                      borderColor: "#4b5563",
                    }}
                  >
                    <RevenueIndicator />
                    {labels.revenue}
                  </td>
                  {chartData.map((item, idx) => (
                    <td
                      key={idx}
                      className="!border !border-border px-0.5 py-1 text-center text-[9px]"
                      style={{ color: "#ffffff", borderColor: "#4b5563" }}
                    >
                      {item.revenue != null
                        ? item.revenue.toLocaleString()
                        : ""}
                    </td>
                  ))}
                </tr>
                <tr className="hover:bg-muted/20 transition-colors">
                  <td
                    className="!border !border-border px-1 py-1 font-normal text-[9px] text-left whitespace-nowrap"
                    style={{
                      color: "#ffffff",
                      borderColor: "#4b5563",
                    }}
                  >
                    <TransactionIndicator />
                    {labels.transaction}
                  </td>
                  {chartData.map((item, idx) => (
                    <td
                      key={idx}
                      className="!border !border-border px-0.5 py-1 text-center text-[9px]"
                      style={{ color: "#ffffff", borderColor: "#4b5563" }}
                    >
                      {item.transaction != null
                        ? item.transaction.toLocaleString()
                        : ""}
                    </td>
                  ))}
                </tr>
                <tr className="hover:bg-muted/20 transition-colors">
                  <td
                    className="!border !border-border px-1 py-1 font-normal text-[9px] text-left whitespace-nowrap"
                    style={{
                      color: "#ffffff",
                      borderColor: "#4b5563",
                    }}
                  >
                    <ATVIndicator />
                    {labels.atv}
                  </td>
                  {chartData.map((item, idx) => (
                    <td
                      key={idx}
                      className="!border !border-border px-0.5 py-1 text-center text-[9px]"
                      style={{ color: "#ffffff", borderColor: "#4b5563" }}
                    >
                      {item.atv != null
                        ? item.atv === 0
                          ? "0"
                          : item.atv.toFixed(2)
                        : ""}
                    </td>
                  ))}
                </tr>
                <tr className="hover:bg-muted/20 transition-colors">
                  <td
                    className="!border !border-border px-1 py-1 font-normal text-[9px] text-left whitespace-nowrap"
                    style={{
                      color: "#ffffff",
                      borderColor: "#4b5563",
                    }}
                  >
                    <ALSIndicator />
                    {labels.als}
                  </td>
                  {chartData.map((item, idx) => (
                    <td
                      key={idx}
                      className="!border !border-border px-0.5 py-1 text-center text-[9px]"
                      style={{ color: "#ffffff", borderColor: "#4b5563" }}
                    >
                      {item.als != null
                        ? item.als === 0
                          ? "0"
                          : item.als.toFixed(2)
                        : ""}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
            <div
              className="text-center py-0.5 mt-2 text-[9px] font-bold"
              style={{ color: "#ffffff" }}
            >
              Months
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );

};

export default ReusableKPIChart;